import { useCallback, useMemo, useState, MouseEvent, useEffect } from "react";
import CreateGroupModal from "admin/components/user-management/CreateGroupModal";
import GroupDrawer from "admin/components/user-management/GroupDrawer";
import { getSortIcon } from "admin/components/user-management/UserTable";
import useCanEditAdminApp from "admin/hooks/useCanEditAdminApp";
import useGroups from "admin/hooks/useGroups";
import { UserGroup } from "admin/types/interfaces/user";
import { excludeSystemTags } from "admin/utils/tags";
import { AlertCircle, Plus, Search, Users, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import useAppHeader from "~/contexts/header";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";

const PAGE_TITLE = "Groups";

export enum GROUP_DRAWER_TABS {
  details = "details",
  members = "members",
  access = "access",
  administration = "administration",
}

type SortDirection = "asc" | "desc";
type SortableColumn = "groupName" | "members" | "created" | null;

const COLUMNS = [
  {
    id: "groupName",
    label: "Group Name",
    className: "",
    loadingClassName: "max-w-40",
    sortable: true,
  },
  {
    id: "description",
    label: "Description",
    className: "",
    loadingClassName: "max-w-64",
    sortable: false,
  },
  {
    id: "members",
    label: "Members",
    className: "w-30",
    loadingClassName: "max-w-20",
    sortable: true,
  },
  {
    id: "created",
    label: "Created",
    className: "w-24",
    loadingClassName: "max-w-20",
    sortable: true,
  },
];

const LoadingElement = ({ loadingClassName }: { loadingClassName: string }) => {
  return <div className={`h-4 bg-muted animate-pulse rounded w-full ${loadingClassName}`} />;
};

const GroupTableLoadingSkeleton = () => {
  return (
    <>
      {Array.from({ length: 25 }).map((_, index) => (
        <TableRow key={`loading-${index}`}>
          {COLUMNS.map((column) => (
            <TableCell key={column.id} className={column.className}>
              <LoadingElement loadingClassName={column.loadingClassName} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
};

const Groups = ({ isNextGen = false }: { isNextGen?: boolean }) => {
  // The action button renders in the page for the legacy admin app, or in the
  // nextgen header (via useNextGenHeader below) — never both.
  const maxContentWidth = isNextGen ? NEXTGEN_MAX_CONTENT_WIDTH : undefined;

  useDocumentTitle(PAGE_TITLE);
  useAppHeader({ title: PAGE_TITLE });

  useEffect(() => {
    trackEvent("admin_groups_viewed");
  }, []);

  const canEdit = useCanEditAdminApp();

  const { data: groups = [], isLoading, error: groupsError } = useGroups();

  const [searchTerm, setSearchTerm] = useState("");
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortableColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const [selectedGroupForDetails, setSelectedGroupForDetails] = useState<UserGroup | null>(null);
  const [isGroupDetailsDrawerOpen, setIsGroupDetailsDrawerOpen] = useState(false);
  const [detailsDrawerDefaultTab, setDetailsDrawerDefaultTab] = useState<GROUP_DRAWER_TABS>(
    GROUP_DRAWER_TABS.details
  );

  const visibleGroups = excludeSystemTags(groups);

  const filteredGroups = useMemo(() => {
    let filtered = visibleGroups.filter((group) => {
      const matchesSearch =
        group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        group.display_name?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    if (sortColumn && sortDirection) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortColumn) {
          case "groupName":
            aValue = (a.display_name || a.name || "").toLowerCase();
            bValue = (b.display_name || b.name || "").toLowerCase();
            break;
          case "members":
            aValue = a.user_count || 0;
            bValue = b.user_count || 0;
            break;
          case "created":
            aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
            bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortDirection === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortDirection === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [visibleGroups, searchTerm, sortColumn, sortDirection]);

  const handleGroupRowClick = (group: UserGroup) => {
    setSelectedGroupForDetails(group);
    setDetailsDrawerDefaultTab(GROUP_DRAWER_TABS.details);
    setIsGroupDetailsDrawerOpen(true);
  };

  const handleUsersClick = (e: MouseEvent, group: UserGroup) => {
    e.stopPropagation(); // Prevent row click
    setSelectedGroupForDetails(group);
    setDetailsDrawerDefaultTab(GROUP_DRAWER_TABS.members);
    setIsGroupDetailsDrawerOpen(true);
  };

  const handleSort = (columnId: string) => {
    const column = COLUMNS.find((col) => col.id === columnId);
    if (!column?.sortable) {
      return;
    }

    if (sortColumn === columnId) {
      // Toggle between asc and desc
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(columnId as SortableColumn);
      setSortDirection("asc");
    }
  };

  const getSortHeaderClasses = (column: (typeof COLUMNS)[number]) => {
    const baseClasses = `flex items-center gap-2 cursor-pointer hover:text-foreground transition-colors select-none group`;
    return sortColumn === column.id
      ? `${baseClasses} text-foreground`
      : `${baseClasses} text-muted-foreground`;
  };

  const handleNewGroupClick = useCallback(() => setCreateGroupModalOpen(true), []);

  // Defined once and placed either in the nextgen header (via useNextGenHeader below)
  // or inline on the page for the legacy admin app — never both.
  const actionButtons = useMemo(
    () =>
      canEdit ? (
        <Button
          onClick={handleNewGroupClick}
          size={isNextGen ? "sm" : "lg"}
          className={cn("flex items-center gap-2", isNextGen && "text-xs")}
        >
          <Plus className={isNextGen ? "h-4 w-4" : "h-5 w-5"} />
          New Group
        </Button>
      ) : null,
    [canEdit, isNextGen, handleNewGroupClick]
  );

  useNextGenHeader(isNextGen ? { actions: [actionButtons] } : {});

  return (
    <div className="min-h-full">
      <div
        className="flex flex-col space-y-6 p-6"
        style={maxContentWidth ? { maxWidth: maxContentWidth, margin: "0 auto" } : undefined}
      >
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              Group Management
            </h1>
            <p className="text-muted-foreground text-lg">Manage user groups and permissions</p>
          </div>

          {!isNextGen && actionButtons && <div className="flex items-center">{actionButtons}</div>}
        </div>

        <div>
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by group name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10 bg-background border border-border"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {groupsError && (
            <div className="p-3 border border-destructive/20 bg-destructive/10 text-destructive rounded-md text-sm flex items-center gap-2 mb-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>Unable to load groups</span>
            </div>
          )}

          <div className="rounded-lg border overflow-x-auto">
            <Table className="min-w-full [&_td]:p-3">
              <TableHeader>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableHead
                      key={column.id}
                      className={cn(column.className, column.sortable && "cursor-pointer")}
                      {...(column.sortable && {
                        onClick: () => handleSort(column.id),
                      })}
                    >
                      {column.sortable ? (
                        <div className={getSortHeaderClasses(column)}>
                          <span>{column.label}</span>
                          <div
                            className={cn(
                              "transition-opacity",
                              sortColumn === column.id
                                ? "opacity-100"
                                : "opacity-0 group-hover:opacity-100"
                            )}
                          >
                            {getSortIcon(column.id, sortColumn, sortDirection)}
                          </div>
                        </div>
                      ) : (
                        column.label
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <GroupTableLoadingSkeleton />
                ) : filteredGroups.length === 0 ? (
                  <TableRow className="pointer-events-none">
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No groups found {searchTerm && `matching "${searchTerm}"`}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredGroups.map((group) => (
                    <TableRow
                      key={group.name}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleGroupRowClick(group)}
                    >
                      <TableCell className="font-medium">
                        {group.display_name || group.name}
                      </TableCell>
                      <TableCell>
                        <span className="text-muted-foreground truncate block max-w-md">
                          {group.description}
                        </span>
                      </TableCell>
                      <TableCell
                        onClick={(e) => handleUsersClick(e, group)}
                        className="text-center"
                      >
                        {group.user_count || 0}
                      </TableCell>
                      <TableCell>
                        {group.created_at ? new Date(group.created_at).toLocaleDateString() : ""}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Showing{" "}
              {filteredGroups.length === visibleGroups.length
                ? "all"
                : `${filteredGroups.length} of ${visibleGroups.length}`}{" "}
              groups
            </p>
          </div>
        </div>
      </div>
      <CreateGroupModal
        isOpen={createGroupModalOpen}
        onClose={() => setCreateGroupModalOpen(false)}
      />
      {selectedGroupForDetails && (
        <GroupDrawer
          isOpen={isGroupDetailsDrawerOpen}
          onClose={() => setIsGroupDetailsDrawerOpen(false)}
          defaultTab={detailsDrawerDefaultTab}
          group={selectedGroupForDetails}
        />
      )}
    </div>
  );
};

export default Groups;
